import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Navigation } from 'lucide-react';

interface EventLocationMapProps {
  latitude: number;
  longitude: number;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
}

const EventLocationMap: React.FC<EventLocationMapProps> = ({ 
  latitude, 
  longitude, 
  locationName,
  locationAddress,
  locationCity
}) => {
  const openGoogleMaps = () => {
    const destinationParts = [locationName, locationAddress, locationCity].filter(Boolean);
    const destination = destinationParts.join(', ').replace(/ /g, '+');
    const origin = locationCity ? locationCity.replace(/ /g, '+') : '';
    const url = origin 
      ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`
      : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, '_blank');
  };
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    if (!mapContainer.current || !latitude || !longitude) return;

    // Initialize map
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
          }
        },
        layers: [{
          id: 'osm',
          type: 'raster',
          source: 'osm',
          minzoom: 0,
          maxzoom: 19
        }]
      },
      center: [longitude, latitude],
      zoom: 15,
      attributionControl: false
    });

    // Create custom marker element
    const markerEl = document.createElement('div');
    markerEl.innerHTML = `
      <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 24 16 24s16-12 16-24c0-8.836-7.164-16-16-16z" fill="hsl(var(--primary))"/>
        <circle cx="16" cy="16" r="6" fill="white"/>
      </svg>
    `;
    markerEl.style.cursor = 'default';

    // Create marker (non-draggable)
    marker.current = new maplibregl.Marker({
      element: markerEl,
      anchor: 'bottom',
      draggable: false
    })
      .setLngLat([longitude, latitude])
      .addTo(map.current);

    // Add popup with location name if provided
    if (locationName) {
      const popup = new maplibregl.Popup({
        offset: [0, -40],
        closeButton: false,
        closeOnClick: false
      }).setHTML(`
        <div style="
          padding: 8px 12px;
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 14px;
          text-transform: uppercase;
        ">
          ${locationName}
        </div>
      `);

      marker.current.setPopup(popup);
      popup.addTo(map.current);
    }

    // Cleanup
    return () => {
      marker.current?.remove();
      map.current?.remove();
    };
  }, [latitude, longitude, locationName]);

  if (!latitude || !longitude) {
    return null;
  }

  return (
    <div className="relative w-full h-full min-h-[160px]">
      <div 
        ref={mapContainer} 
        className="w-full h-full rounded-lg border-2 border-dark"
      />
      <button
        onClick={openGoogleMaps}
        className="absolute top-3 right-3 bg-primary text-primary-foreground p-2 rounded-lg border-2 border-dark shadow-brutal hover:translate-y-0.5 hover:shadow-none transition-all"
        title="Ottieni indicazioni"
      >
        <Navigation className="h-5 w-5" />
      </button>
    </div>
  );
};

export default EventLocationMap;
